import React, { useMemo } from 'react';
import { bool, func, number, oneOfType, string } from 'prop-types';
import classNames from 'classnames';
import { Text } from '@fiverr-private/typography';
import { I18n, translate } from '@fiverr-private/i18n-react';
import { regulationTranslation } from '@fiverr-private/price_regulation_translations';
import { useCalculatePriceBreakdown, BUYABLE_TYPE } from '@fiverr-private/price_upfront_sdk';
import { useGigListingsContext } from '../../../../GigCardListings/context/GigListingsContext';
import { trackGigForClickType } from '../../../utils';
import { priceMarkupFormatter } from '../../../../GigCardListings/ListingsTransformer/priceTransformer';
import { clickedElement } from '../../../Components/Price';
import { useCardContext } from '../../../../GigCardListings/context/CardContext';
import ContextualLink from '../../../Components/ContextualLink';
import TooltipWrapper from '../../../../shared/components/TooltipWrapper';
import { PriceUpfrontTooltip } from '../../../../shared/components/PriceUpfrontTooltip';
import styles from './index.module.scss';

const Price = ({
    price = 0,
    originalPrice,
    url,
    urlTarget,
    pricingFactorAlias,
    onMouseEnter,
    onMouseLeave,
    className = '',
    textClassName = '',
    priceDisplayStatus = 'none',
    auctionId = null,
    showHourlyPrice = false,
    hourlyRate,
}) => {
    const { currencyObj } = useGigListingsContext();
    const { calculatePriceBreakdown } = useCalculatePriceBreakdown();
    const {
        enrichedTracker: { trackEvent },
    } = useCardContext();

    const regulationTranslationText = regulationTranslation(priceDisplayStatus);

    const displayPrice = useMemo(() => {
        if (showHourlyPrice) {
            return hourlyRate ?? '';
        }
        if (!originalPrice) {
            return price;
        }
        const priceBreakdown = calculatePriceBreakdown({ netPrice: originalPrice, buyableType: BUYABLE_TYPE.GIG });
        if (priceBreakdown) {
            return Math.ceil(priceBreakdown.gross_price * currencyObj.rate);
        }
        return price;
    }, [originalPrice, price, calculatePriceBreakdown, currencyObj.rate, showHourlyPrice, hourlyRate]);

    // Hide price if hourly mode is active but no hourly rate
    if (showHourlyPrice && !hourlyRate) {
        return null;
    }

    const clickedPriceElement = clickedElement(displayPrice);
    const showTooltip = !showHourlyPrice && displayPrice !== price;

    return (
        <TooltipWrapper content={<PriceUpfrontTooltip />} shouldRenderTooltip={showTooltip} position="bottom">
            <ContextualLink
                href={url}
                target={urlTarget}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onMouseDown={(e) => trackGigForClickType(e.button, trackEvent, clickedPriceElement)}
                className={classNames('m-t-8', 'tbody-5', styles.link, className)}
                ariaLabel={translate('gig_cards.gig_link_aria_label')}
                auctionId={auctionId}
            >
                <span className={classNames('text-bold', 'co-grey-1200', textClassName)}>
                    {!showHourlyPrice && (
                        <>
                            <I18n k="gig_listings.gig_cards.price.from" />
                            &nbsp;
                        </>
                    )}
                    <span
                        dangerouslySetInnerHTML={{ __html: priceMarkupFormatter({ currencyObj, price: displayPrice }) }}
                    />
                    {showHourlyPrice && <I18n k="gig_listings.gig_cards.price.hourly.suffix" />}
                    {showHourlyPrice && (
                        <Text paddingLeft="1.5" fontWeight="normal" color="bodySecondary" as="span">
                            <I18n k="gig_listings.gig_cards.price.hourly.taxes_and_fees" />
                        </Text>
                    )}
                    {!showHourlyPrice && regulationTranslationText && (
                        <Text paddingLeft="1.5" fontWeight="normal" color="bodySecondary" as="span">
                            {regulationTranslationText}
                        </Text>
                    )}
                </span>

                {!showHourlyPrice && pricingFactorAlias && (
                    <span className={classNames('co-grey-900', styles.pricingFactor)}>
                        &nbsp;/ {pricingFactorAlias}
                    </span>
                )}
            </ContextualLink>
        </TooltipWrapper>
    );
};

Price.propTypes = {
    price: number,
    originalPrice: number,
    url: string,
    urlTarget: string,
    pricingFactorAlias: string,
    onMouseEnter: func,
    onMouseLeave: func,
    className: string,
    textClassName: string,
    priceDisplayStatus: string,
    auctionId: string,
    showHourlyPrice: bool,
    hourlyRate: oneOfType([string, number]),
};

export default Price;
